import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Bell, BellOff, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';

const emptyForm = { title: '', content: '', is_active: true };

export default function AnnouncementsPage() {
    const { hasRole } = useAuth();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editData, setEditData] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const canManage = hasRole('sekretaris', 'ketua_rt', 'wakil');

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/announcements`, { withCredentials: true });
            setAnnouncements(res.data);
        } catch { toast.error('Gagal memuat pengumuman'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditData(null); setForm(emptyForm); setShowForm(true); };
    const openEdit = (a) => { setEditData(a); setForm({ title: a.title, content: a.content, is_active: a.is_active }); setShowForm(true); };

    const handleSave = async () => {
        if (!form.title || !form.content) { toast.error('Isi judul dan isi pengumuman'); return; }
        setSaving(true);
        try {
            if (editData) {
                await axios.put(`${API}/announcements/${editData.id}`, form, { withCredentials: true });
                toast.success('Pengumuman diperbarui');
            } else {
                await axios.post(`${API}/announcements`, form, { withCredentials: true });
                toast.success('Pengumuman dibuat');
            }
            setShowForm(false); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Hapus pengumuman ini?')) return;
        try {
            await axios.delete(`${API}/announcements/${id}`, { withCredentials: true });
            toast.success('Dihapus'); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };

    const toggleActive = async (a) => {
        try {
            await axios.put(`${API}/announcements/${a.id}`, { ...a, is_active: !a.is_active }, { withCredentials: true });
            load();
        } catch (e) { toast.error('Gagal mengubah status'); }
    };

    return (
        <div className="space-y-5" data-testid="announcements-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="page-header text-2xl">Pengumuman</h1>
                    <p className="text-sm text-slate-500 mt-1">{announcements.length} pengumuman terdaftar</p>
                </div>
                {canManage && (
                    <Button onClick={openAdd} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-announcement-btn">
                        <Plus className="w-4 h-4" /> Buat Pengumuman
                    </Button>
                )}
            </div>

            <div className="space-y-3">
                {loading ? [...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />) :
                 announcements.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
                        <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-400 font-medium">Belum ada pengumuman</p>
                        {canManage && <p className="text-slate-400 text-sm mt-1">Klik "Buat Pengumuman" untuk membuat pengumuman pertama</p>}
                    </div>
                ) : announcements.map(a => (
                    <div key={a.id} data-testid={`announcement-item-${a.id}`}
                        className={`bg-white rounded-xl border p-5 transition-all ${a.is_active ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${a.is_active ? 'bg-amber-100' : 'bg-slate-100'}`}>
                                    {a.is_active ? <Bell className="w-4 h-4 text-amber-600" /> : <BellOff className="w-4 h-4 text-slate-400" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <p className="font-bold text-slate-800">{a.title}</p>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                            {a.is_active ? 'Aktif' : 'Nonaktif'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-600 leading-relaxed">{a.content}</p>
                                    <p className="text-xs text-slate-400 mt-2">
                                        Dibuat oleh {a.creator_name} &mdash; {new Date(a.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </p>
                                </div>
                            </div>
                            {canManage && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <button onClick={() => toggleActive(a)} title={a.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500 transition-colors" data-testid={`toggle-announcement-${a.id}`}>
                                        {a.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                    <button onClick={() => openEdit(a)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600 transition-colors" data-testid={`edit-announcement-${a.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(a.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500 transition-colors" data-testid={`delete-announcement-${a.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bell className="w-5 h-5 text-primary" />
                            {editData ? 'Edit Pengumuman' : 'Buat Pengumuman Baru'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label className="font-semibold">Judul *</Label>
                            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="Judul pengumuman" data-testid="announcement-title-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="font-semibold">Isi Pengumuman *</Label>
                            <Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                                placeholder="Tulis isi pengumuman di sini..." rows={4} data-testid="announcement-content-input" />
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                                data-testid="announcement-active-toggle" />
                            <Label className="font-medium">{form.is_active ? 'Tampilkan di halaman publik' : 'Sembunyikan dari publik'}</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>Batal</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-announcement-btn">
                            {saving ? 'Menyimpan...' : 'Simpan'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
